import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { useState } from 'react'
import { PopupState } from 'material-ui-popup-state/hooks'
import { useForm } from 'react-hook-form'
import { TextField } from '@common/components/Form/TextField'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { CopyAsIntrareadIcon } from '@common/components/Icons'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  padding: 1,
}

const CopyIntraReadValidationMessage = 'Provide a reason'

const CopyIntraReadValidationSchema = z.object({
  reason: z.string().min(1, CopyIntraReadValidationMessage),
})

export default function CopyIntraReadModal({
  popupState,
}: {
  task_id: string
  study_id: string
  popupState: PopupState
}) {
  const [open, setOpen] = useState(false)
  const {
    control,
    watch,
    formState: { isValid },
    setError,
  } = useForm({ mode: 'onChange', defaultValues: { reason: '' }, resolver: zodResolver(CopyIntraReadValidationSchema) })
  const reason = watch('reason')
  const handleClose = () => {
    setOpen(false)
    popupState.close()
  }

  const handledCopyIntraReadTask = async (reason: string) => {
    if (!isValid) {
      setError('reason', {
        message: CopyIntraReadValidationMessage,
      })
      return
    }
    console.log(reason)
  }

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          width: '100%',
          gap: 1.5,
          alignItems: 'center',
        }}
        data-testid="copy-intra-read-task-button"
        tabIndex={0}
      >
        <CopyAsIntrareadIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
        <Typography variant="body1">Copy as intra-read</Typography>
      </Box>
      <Modal open={open} data-testid="copy-intra-read-modal">
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              alignSelf: 'stretch',
              width: 600,
            }}
          >
            <Typography variant="h6" color="text.primary" id="copy-intra-read-modal-title">
              Copy task as intra-read?
            </Typography>
            <Typography variant="body1" color="text.secondary" id="copy-intra-read-modal-description">
              A copy of this task with the completed eCRF information will be created and assigned to the same reader.
            </Typography>
          </Box>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              padding: 2,
            }}
            noValidate
            autoComplete="off"
          >
            <TextField
              name="reason"
              control={control}
              id="outlined-multiline-static"
              label="Provide a reason"
              multiline
              rows={6}
              fullWidth
              onKeyDown={(e) => e.stopPropagation()}
              data-testid="reason-textfield"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              alignSelf: 'stretch',
              padding: 2,
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="cancel-copy-intra-read-task-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              onClick={() => handledCopyIntraReadTask(reason)}
              data-testid="confirm-copy-intra-read-task-button"
            >
              Copy
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
